import { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import TableRow from "@mui/material/TableRow";
import InteractionIntervalSetting from "../../../models/communications/InteractionIntervalSetting";
import SchedulingMatrixRow from "../../../models/communications/SchedulingMatrixRow";
import MatrixCellControlsBox from "./controls/MatrixCellControlsBox";
import MatrixInteractionControls from "./controls/MatrixInteractionControls";
import MatrixCell from "./MatrixCell";

import ConditionBar from "../../conditions/ConditionBar";
import InsertionPoint from "../../InsertionPoint";
import { useStoreDispatch, useStoreSelector } from "../../../store/Hooks";
import useHttp from "../../../hooks/useHttp";
import { useDrag, DropTargetMonitor, DragSourceMonitor } from "react-dnd";
import { InteractionNodeDragItem } from "../../../helpers/DragHelper";
import Interval from "../../../models/communications/Interval";
import {
  findCommSetEarliestStartDay,
  getOrderedIntervalsForCommSet,
} from "../../../helpers/IntervalSetHelper";
import InteractionNode from "../../../models/interactions/InteractionNode";
import InteractionNodeInterval from "../../../models/interactions/InteractionNodeInterval";

export const MatrixInteractionRowStyled = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    cursor: "grab",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const MatrixInteractionRow = (props: SchedulingMatrixRow) => {
  /* start reordering logic */
  const intervalsToCommSetsMap = new Map<number | null, Array<Interval>>();

  if (props.associatedCommSets) {
    for (let i = 0; i < props.associatedCommSets.length; i++) {
      const thisHereCommSet = props.associatedCommSets[i];
      let key = -1;
      if (thisHereCommSet.id) {
        key = thisHereCommSet.id;
      }
      const intervalsForThisCommSet = getOrderedIntervalsForCommSet(
        key,
        props.associatedIntervals || [],
      );
      intervalsToCommSetsMap.set(key, intervalsForThisCommSet);
    }

    // now we need to add intervals that have null commset
    // use null as the key
    const intervalsForThisCommSet = getOrderedIntervalsForCommSet(
      null,
      props.associatedIntervals || [],
    );
    intervalsToCommSetsMap.set(null, intervalsForThisCommSet);
  }

  const commSetsMapEntries = [...intervalsToCommSetsMap.entries()];
  commSetsMapEntries.sort((a, b) => {
    const startDayA = findCommSetEarliestStartDay(
      a[0],
      props.associatedIntervals || [],
    );
    const startDayB = findCommSetEarliestStartDay(
      b[0],
      props.associatedIntervals || [],
    );

    return startDayA - startDayB;
  });

  const sortedMap = new Map(commSetsMapEntries); // ***this successfully re-orders the commset top row
  const associatedIntervalsReordered = [];

  for (const objArray of sortedMap.values()) {
    // Iterate over the array of objects and create JSX elements
    const newAssociatedIntervals = objArray.map(
      (associatedInterval: Interval) => {
        return associatedInterval;
      },
    );

    // Concatenate the JSX elements to the new array
    associatedIntervalsReordered.push(...newAssociatedIntervals);
  }

  const intervalSettingsCopyReorderedCopy =
    associatedIntervalsReordered.slice();
  const intervalSettingsCopy = props.intervalSettings.slice();
  intervalSettingsCopy.sort((a, b) => {
    // Find the indices of a and b's interval_id in arrayA
    const indexA = intervalSettingsCopyReorderedCopy.findIndex(
      (item) => item.id === a.interval_id,
    );
    const indexB = intervalSettingsCopyReorderedCopy.findIndex(
      (item) => item.id === b.interval_id,
    );

    // Compare the indices to sort arrayB
    return indexA - indexB;
  });
  // console.log(intervalSettingsCopy)
  // replace matrixCellRendering with jsxArray
  /* end reordering logic */

  const num_intervals = props.intervalSettings.length;
  const cell_width_percentage = 100 / (num_intervals + 2);
  const tempInteractionNode = props.interaction_node;

  const initialStyle = {
    opacity: 0,
    height: "0px",
    margin: "0px",
    padding: "0px",
  };

  const [{ isDragging, item }, drag] = useDrag({
    type: "INTERACTION", // Make sure to define the type here
    item: new InteractionNodeDragItem(
      props.interaction_node,
      props.interaction,
      props.interaction_node ? props.interaction_node.ordinal : 1,
      null,
      intervalSettingsCopy,
      props.intervalSetID,
    ),
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: !!monitor.isDragging(),
      item: monitor.getItem(),
    }),
  });

  const somethingInAppIsDragging = item !== null;

  const [rootStyle, setRootStyle] = useState<any>(initialStyle);
  const handleOnMouseEnter = () => {
    // console.log(props.ordinal);

    if (!somethingInAppIsDragging) {
      setRootStyle({});
    }
  };

  const handleOnMouseLeave = () => {
    if (!somethingInAppIsDragging) {
      setRootStyle(initialStyle);
    }
  };

  useEffect(() => {
    if (somethingInAppIsDragging) {
      setRootStyle({});
    } else {
      setRootStyle(initialStyle);
    }
  }, [somethingInAppIsDragging]);

  const associatedIntervalIDsForThisInteractionRow: Array<number> =
    intervalSettingsCopy.map((intervalSetting: InteractionIntervalSetting) => {
      return intervalSetting.interval_id;
    });

  // if this row type in an insertion point,
  // return a simple TableRow with insertion point of type "intervalSet"
  if (props.insertionPoint) {
    return (
      <InsertionPoint
        interactionNode={tempInteractionNode}
        interaction={props.interaction}
        type={"intervalSet"}
        insertFunction={props.addNewInteractionToIntervalSet}
        associatedIntervalIDs={associatedIntervalIDsForThisInteractionRow}
        intervalSetID={props.intervalSetID}
        ordinal={props.ordinal}
        rootStyle={rootStyle}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        intervalSettings={intervalSettingsCopy}
      />
    );
  } else {
    return (
      <MatrixInteractionRowStyled ref={drag}>
        {!isDragging && (
          <MatrixCell
            sx={{
               width: cell_width_percentage * 2 + "%", 
               padding: "5px",
               position: "sticky",
               left: 0,
               zIndex: 2,
               backgroundColor: "#fafafa"
              }}
          >
            {props.condition_id !== null && (
              <ConditionBar
                sx={{
                  position: "relative",
                  top: -5,
                  left: -6,
                  width: "calc(100% + 12px)",
                }}
                table={InteractionNode}
                tableID={props.interaction_node.id}
              />
            )}
            <MatrixInteractionControls
              handleDeleteInteraction={props.handleDeleteInteraction}
              condition_id={props.condition_id}
              interaction={props.interaction}
              interaction_node={props.interaction_node}
            />
          </MatrixCell>
        )}
        {!isDragging &&
          intervalSettingsCopy.map(
            (interactionIntervalSetting: InteractionIntervalSetting) => (
              <MatrixCell
                sx={{
                  width: cell_width_percentage + "%",
                  padding: "0px",
                  margin: "0px",
                }}
                key={interactionIntervalSetting.interaction_node_interval_id}
              >
                {interactionIntervalSetting.condition_id !== null && (
                  <ConditionBar
                    sx={{
                      position: "relative",
                      left: -6,
                      top: -16,
                      width: "calc(100% + 12px)",
                    }}
                    table={InteractionNodeInterval}
                    tableID={
                      interactionIntervalSetting.interaction_node_interval_id
                    }
                  />
                )}
                {interactionIntervalSetting.selected}
                <MatrixCellControlsBox
                  {...interactionIntervalSetting}
                  interval_set_id={props.intervalSetID}
                />
              </MatrixCell>
            ),
          )}
      </MatrixInteractionRowStyled>
    );
  }
};
export default MatrixInteractionRow;
